import { jsx as _jsx } from "react/jsx-runtime";
import { Button as RecessButton, IconButton as RecessIconButton, TextLink as RecessTextLink, } from '@guildeducationinc/recess';
import { Button as MuiButton, Tooltip } from '@mui/material';
import { useRef } from 'react';
import { trackClickEvent } from '../../utilities/analytics';
/**
 * A button component that tracks click events and supports multiple button types.
 * This should be used in place of recess/mui buttons where tracking is needed.
 *
 * @param {string} enText - The English text for the button.
 * @param {string} tag - The tag associated with the button for tracking purposes.
 * @param {string} [btnType='recess'] - The type of the button. Can be 'recess', 'icon', 'link', or 'mui'.
 * @param {React.ReactNode} children - The child elements to be rendered inside the button.
 * @param {React.MouseEventHandler} onClick - The click event handler for the button.
 * @param {TrackableButtonProps} rest - Additional props to be passed to the button component.
 * @param {boolean} [showTooltipForIcon=false] - Whether to show a tooltip for the icon button.
 *
 * @returns {JSX.Element} The rendered button component.
 */
export const TrackableButton = ({ enText, tag, btnType = 'recess', children, onClick, showTooltipForIcon = false, ...rest }) => {
    const ref = useRef(null);
    const handleClick = (e) => {
        let localizedText = '';
        const extraData = {};
        let url;
        if (ref.current) {
            localizedText = ref.current.textContent ?? '';
        }
        switch (btnType) {
            case 'icon':
                localizedText = rest.label;
                break;
            case 'link':
                url = rest.href || rest.to;
                break;
            case 'recess':
                url = rest.href || rest.to;
                break;
        }
        if (url) {
            extraData.url = url;
        }
        trackClickEvent(enText, tag, 'button', localizedText, extraData);
        if (onClick) {
            onClick(e);
        }
    };
    if (btnType === 'mui') {
        return (_jsx(MuiButton, { ref: ref, ...rest, onClick: handleClick, children: children }));
    }
    if (btnType === 'icon') {
        return (_jsx(Tooltip, { title: showTooltipForIcon ? rest.label : '', children: _jsx(RecessIconButton, { ...rest, onClick: handleClick, children: children }) }));
    }
    if (btnType === 'link') {
        return (_jsx(RecessTextLink, { forwardRef: ref, ...rest, onClick: handleClick, children: children }));
    }
    return (_jsx(RecessButton, { forwardRef: ref, ...rest, onClick: handleClick, children: children }));
};
